import { handleResponseOld, handleResponseWithNon200Errors, makeGet, makePost } from './helpers';
import take from 'lodash/take';

/**
 * @category Item API
 * @see fetchFollowerCount
 */
type FetchFollowerCountParams = {
    authToken: string;
    deployment: string;
    sellerIds: number[];
};
/**
 * fetches follower count for given seller IDs via Post to Item API service
 * @function fetchFollowerCount
 * @category Item API
 * @param {FetchFollowerCountParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/follower-count
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/bidder/routes/post-follower-count.ts
 */
const fetchFollowerCount = ({ deployment, sellerIds }: FetchFollowerCountParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ deployment, path: '<ITEM-API>follower-count/' });
        request.send({ sellerIds });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see fetchSellers
 */
type FetchSellersParams = {
    deployment: string;
};
/**
 * fetches list of all sellers via Get to Item API service
 * @function fetchSellers
 * @category Item API
 * @param {FetchSellersParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/spa/small/sellers
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/inventory/routes/get-all-sellers.ts
 */
const fetchSellers = ({ deployment }: FetchSellersParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({ deployment, path: '<ITEM-API>spa/small/sellers' });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see fetchSellersByIds
 */
type FetchSellersByIdsParams = {
    authToken: string;
    deployment: string;
    sellerIds: number[];
};
/**
 * fetches list of sellers by given sellerIds via Post to Item API service
 * @function fetchSellersByIds
 * @category Item API
 * @param {FetchSellersByIdsParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/spa/small/sellers
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/react-site/routes/post-spa-sellers.ts
 */
const fetchSellersByIds = ({ authToken, deployment, sellerIds }: FetchSellersByIdsParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ authToken, deployment, path: '<ITEM-API>spa/small/sellers' });
        request.send({ ids: sellerIds });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Personalization API
 * @see postFollow
 */
type PostFollowParams = {
    authToken: string;
    deployment: string;
    sellerId: number;
};
/**
 * follows a house for given sellerId via Post to Personalization API
 * @function postFollow
 * @category Personalization API
 * @param {PostFollowParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/personalization/followhouse
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/tq7yn802h4/resources/dw0ss8/methods/POST
 * @see https://github.com/LIVEauctioneers/personalization-api/tree/master/functions/PersonalizationFollowHouse
 */
const postFollow = ({ authToken, deployment, sellerId }: PostFollowParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: 'followhouse',
            authToken,
            deployment,
            path: '<PERSONALIZATION-API>',
        });
        request.send({ houseId: sellerId });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category Mainhost API
 * @see postMessage
 */
type PostMessageParams = {
    authToken: string;
    deployment: string;
    messageDetails: any;
};
/**
 * Sends a message to a house for given messageDetails via Post to Mainhost API
 * @function postMessage
 * @category Mainhost API
 * @param {PostMessageParams}
 * @see https://mainhost-DEPLOYMENT.liveauctioneers.com/api/s_msg
 * @see https://github.com/LIVEauctioneers/mainhost-prod/blob/master/libs/MOBILE_API.php#L822
 */
const postMessage = ({ authToken, deployment, messageDetails }: PostMessageParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: '<MAINHOST-API>api/s_msg',
            queryParams: { client_version: '4.3' },
        });

        if (!messageDetails) {
            reject(new Error('no message details sent'));
        }

        const { catalogId, files, itemId, message, sellerId, subject } = messageDetails;

        request
            .field('cat_id', catalogId || 0)
            .field('cc_sender', false)
            .field('house_id', sellerId)
            .field('lot_id', itemId || 0)
            .field('message_text', message)
            .field('subject', subject);

        take(files || [], 5).forEach((f, idx) => {
            const fieldName = idx === 0 ? 'attach' : `attach${idx}`;
            request.attach(fieldName, f);
        });

        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Personalization API
 * @see postUnfollow
 */
type PostUnfollowParams = {
    authToken: string;
    deployment: string;
    sellerId: number;
};
/**
 * unfollows a house for given sellerId via Post to Personalization API
 * @function postUnfollow
 * @category Personalization API
 * @param {PostFollowParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/personalization/unfollowhouse
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/tq7yn802h4/resources/xl8itb/methods/POST
 * @see https://github.com/LIVEauctioneers/personalization-api/tree/master/functions/PersonalizationUnfollowHouse
 */
const postUnfollow = ({ authToken, deployment, sellerId }: PostUnfollowParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: 'unfollowhouse',
            authToken,
            deployment,
            path: '<PERSONALIZATION-API>',
        });
        request.send({ houseId: sellerId });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

export default {
    fetchFollowerCount,
    fetchSellers,
    fetchSellersByIds,
    postFollow,
    postMessage,
    postUnfollow,
};
